import { createContext, ReactNode } from 'react';
import { ScanRatio } from 'core/flow/entities';
import { AppEnvironment } from '../entities';

export type Config = {
  appVersion: string;
  appEnvironment: AppEnvironment;
  geocodeUrl: string;
  geocodeApiKey: string;
  apiUrl: string;
  apiPrefix: string;
  applePayContainerId: string;
  googlePayContainerId: string;
  pixAutomaticoContainerId: string;
  pixQrContainerId: string;
  appName: string;
  mainAppUrl: string;
  rootDomain: string;
  funnelPlatform: string;
  analytics: {
    amplitudeApiKey: string;
    amplitudeApiEndpoint: string;
  };
  supportCenter: string;
  dateOfMoneyBack: string;
  appleSupport: string;
  supportEmail: string;
  legalSupportEmail: string;
  projectName: string;
  contactEmails: {
    general: string;
    pr: string;
    hiring: string;
  };
  manageSubscriptionUrl: string;
  googleSupport: string;
  askNebulaUrl: string;
  testingTokenCookieKey: string;
  expensivePriceTreshold: number;
  scanRatio: {
    [key in ScanRatio]: {
      width: number;
      height: number;
    };
  };
  palmAnimationDuration: number;
  isProduction: boolean;
  isStage: boolean;
  isDevelopment: boolean;
  isBuildPhase: boolean;
  sentryDsn: string;
  addressUS: string;
  addressCN: string;
  address: string;
  cdn: string;
  growthbook: {
    apiHost: string;
    clientKey: string;
    decryptionKey: string;
  };
  oneTrust: {
    key: string;
  };
  funnelBuilder: {
    apiHost: string;
    apiKey: string;
    requestTimeout: number;
    maxRequestRetries: number;
    requestRetryDelay: number;
    isLanguageScoped: boolean;
  };
  SVGSprite: {
    inputPath: string;
    outputPath: string;
    typesOutputPath: string;
    outputName: `${string}.svg`;
  };
  domainUrl: string;
  grafanaLogger: {
    url: string;
    enabled?: boolean;
  };
  observability: {
    defaultTracerName: string;
    tracesUrl: string;
    browser: {
      httpInstrumentationIgnoreUrls: Array<string | RegExp>;
      httpInstrumentationSpanRate: number;
    };
    server: {
      httpInstrumentationIgnoreOutGoingUrls: Array<string | RegExp>;
      httpInstrumentationIgnoreIncomingUrls: Array<string | RegExp>;
      httpInstrumentationSpanRate: number;
    };
  };
  errorMonitoring: {
    enabled: boolean;
    ignoreErrors: Array<RegExp>;
    ignoreTraces: Array<string>;
  };
  buildCache: {
    path: string;
  };
  ebanxUrl: string;
  clarityId: string;
  verificationIds: {
    trustpilot: string;
    facebook: string;
  };
  supportForm: {
    id: number;
    descriptionMaxLength: number;
    maxAttachments: number;
    maxAttachmentSizeBytes: number;
    allowedMimeTypes: string[];
  };
  funnelBuilderTrustedOrigins: string[];
  companyName: string;
  companyNameUS: string;
  companyNameCN: string;
  supportPhoneNumber: string;
  recurringPayment: {
    refetchDelay: number;
    maxRetryAttempts: number;
  };
  paymentTrustedOrigin: string;
  emailConsentRequestDelay: number;
  funnelsWithMinimalAnalytics: Array<string>;
  funnelsWithoutClarity: Array<string>;
  maxNameLength: number;
  scrollTrackingThresholdsPercents: Array<number>;
  activePaymentStatuses: Array<string>;
  marketing: {
    fallbackEltv: number;
  };
  techLinks: {
    terms: string;
    privacyPolicy: string;
    fullDisclaimer: string;
    impressum: string;
    legalCenter: string;
    faq: string;
    moneyBackPolicy: string;
    cookiePolicy: string;
  };
  legalDisclosure: {
    companyName: string;
    address: string;
    jurisdiction: string;
    registrationNumber: string;
    vatNumber?: string;
  };
  partnersLinks: {
    glamour: string;
    marieClaire: string;
    cosmopolitan: string;
    vogue: string;
    forbes: string;
    newYorkTimes: string;
    newYorkPost: string;
    grazia: string;
    bustle: string;
    huffpost: string;
    popsugar: string;
  };
  web2appLink: string;
  redis: {
    master: { url: string; staleAge: number };
    replicas: { url: string; staleAge: number };
    funnelBuilder: {
      url: string;
      staleAge: number;
      commandTimeoutMs: number;
      disableOfflineQueue: boolean;
    };
  };
  revalidation: {
    pathsPerRequest: number;
  };
  funnelsCdn: {
    pipelineUrl: string;
    service: string;
    token: string;
  };
  forceEnableLRU: string;
  legalFlows: Array<string>;
};

export const ConfigContext = createContext<Config>(null!);

export type ConfigProviderProps<T extends Config> = {
  children?: ReactNode;
  config: T;
};

export const ConfigProvider = <T extends Config>({ config, children }: ConfigProviderProps<T>) => {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
};
